// i18n
import { defaultLocale, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import listTranslations from '../PricingList/i18n.json';

import {
  Currency,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import { SyntheticEvent, useMemo, useState } from 'react';
import {
  downloadsPath,
  featurePath,
  helpPath,
  productBookingPath,
} from 'presentations/routes/path';
import { TrialChannel } from 'domains/models/subscription/trial_channel';
import JicooBrandingFeature from 'domains/models/subscription/features/jicoo_branding';
import SalesforceIntegrationFeature from 'domains/models/subscription/features/integrations/salesforce';
import EventTypeRoundRobinFeature from 'domains/models/subscription/features/event_type_round_robin';
import { PlanVersion } from 'domains/models/subscription/planVersion';
import { useRouter } from 'next/router';
import { developerApiWebhookPath } from 'presentations/routes/path/developer';

export interface Props {
  onClickPrice?: any;
  planId: PlanId | null;
  planVersion: PlanVersion | null;
  currency: Currency;
  intervalType: IntervalType;
  isLoading?: boolean;
  isLogin: boolean;
  canChange: boolean;
  trialStartedAt: string | null | undefined;
  trialEndedAt: string | null | undefined;
  trialChannel: TrialChannel | null | undefined;
  currentIntervalType: IntervalType | null;
  isChangeAnnualToMonth: boolean;
}
interface SectionRow {
  title: string;
  info?: string;
  url?: string;
  checks: (boolean | number | string)[];
}
interface Section {
  title: string;
  functions: SectionRow[];
}

export function usePricingTable(props: Props) {
  const t = translateInComponent([translations, listTranslations]);
  const [anchor, setAnchor] = useState<any>(null);
  const [description, setDescription] = useState<string | null>(null);

  const plans = [PlanId.Free, PlanId.Pro, PlanId.Team, PlanId.Enterprice];
  const router = useRouter();
  const locale = router.locale;

  const getAvailables = (planId: PlanId) => {
    const max = plans.indexOf(planId);
    const availabilities = [];
    for (let index = 0; index < plans.length; index++) {
      availabilities.push(index >= max ? true : false);
    }
    return availabilities;
  };

  const allUnlimited = [
    t('unlimited'),
    t('unlimited'),
    t('unlimited'),
    // t('unlimited'),
    t('unlimited'),
  ];

  const sections: Section[] = useMemo(
    () => [
      {
        title: 'Core functions',
        functions: [
          {
            title: t('Multiple calendar account'),
            checks: getAvailables(PlanId.Free),
            info: t('Multiple calendar account description'),
          },
          {
            title: t('Calendar connections per user'),
            checks: [3, 3, 6, 6],
            info: t('Calendar connections per user description'),
          },
          {
            title: t('Active Booking Pages'),
            checks: allUnlimited,
          },
          {
            title: t('Scheduled meetings'),
            checks: allUnlimited,
            info: t('Scheduled meetings description'),
          },
          {
            title: t('1-on-1 event_type'),
            checks: getAvailables(PlanId.Free),
            url: featurePath('1-on-1-scheduling'),
          },
          {
            title: t('One-off meetings'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Booking Request'),
            checks: getAvailables(PlanId.Free),
            info: t('Booking Request description'),
            // url: featurePath('meeting-poll'),
          },
          {
            title: t('Meeting Poll'),
            checks: getAvailables(PlanId.Free),
            info: t('Meeting Poll description'),
            url: featurePath('meeting-poll'),
          },
          {
            title: t('Cancellation'),
            checks: getAvailables(PlanId.Free),
            url: featurePath('cancellation'),
          },
          {
            title: t('Embed options'),
            checks: getAvailables(PlanId.Free),
            info: t('Embed options description'),
            url: featurePath('embed-options-to-web-site'),
          },
          {
            title: t('Time zone support'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('English support'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Terms and Policy'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Messaging'),
            checks: getAvailables(PlanId.Free),
            info: t('Messaging description'),
          },
          {
            title: t('Mac app'),
            description: t('Mac app description'),
            checks: getAvailables(PlanId.Free),
            url: downloadsPath(),
          },
          {
            title: t('Jicoo AI'),
            checks: getAvailables(PlanId.Pro),
            info: t('Jicoo AI description'),
            url: featurePath('jicoo-ai'),
          },
          {
            title: t('Custom forms'),
            checks: getAvailables(PlanId.Pro),
            info: t('Custom forms description'),
            url: featurePath('customize-contact-form'),
          },
          {
            title: t('Search'),
            checks: getAvailables(PlanId.Pro),
            info: t('Search description'),
            url: featurePath('search'),
          },
        ],
      },
      {
        title: 'Teams',
        functions: [
          {
            title: t('Number of users'),
            checks: allUnlimited,
          },
          {
            title: t('Routing Form'),
            checks: getAvailables(PlanId.Free),
            info: t('Routing Form description'),
            url: featurePath('routing-form'),
          },
          {
            title: t('Form Export'),
            checks: getAvailables(PlanId.Free),
            info: t('Form Export description'),
            url: featurePath('export-csv'),
          },
          {
            title: t('Schedule by proxy'),
            checks: getAvailables(PlanId.Pro),
            info: t('Schedule by proxy description'),
          },
          {
            title: t('Collective event types'),
            checks: getAvailables(PlanId.Pro),
            info: t('Collective event types description'),
            url: featurePath('team-scheduling'),
          },
          {
            title: t('Secure online payment'),
            description: t('Secure online payment description'),
            url: productBookingPath('#payment'),
            checks: getAvailables(PlanId.Team),
          },
          {
            title: t('Redirect invitees'),
            checks: getAvailables(PlanId.Team),
            info: t('Redirect invitees description'),
          },
          {
            title: t('Group event types'),
            checks: getAvailables(PlanId.Team),
            info: t('Group event types description'),
            url: featurePath('specified-number-of-seats'),
          },
          {
            title: t('Round robin event types'),
            checks: getAvailables(EventTypeRoundRobinFeature.currentPlanId),
            info: t('Round robin event types description'),
            url: featurePath('round-robin'),
          },
          {
            title: t('Resource Set'),
            checks: getAvailables(PlanId.Team),
            info: t('Resource Set description'),
          },
          {
            title: t('Follow up'),
            checks: getAvailables(PlanId.Team),
            info: t('Follow up description'),
            url: featurePath('follow-up'),
          },
          {
            title: t('Keyword Block'),
            checks: getAvailables(PlanId.Team),
            info: t('Keyword Block description'),
          },
          {
            title: t('Acl support'),
            checks: getAvailables(PlanId.Team),
            info: t('Acl support description'),
          },
        ],
      },
      {
        title: 'Customization',
        functions: [
          {
            title: t('Rich design of the booking form'),
            checks: getAvailables(PlanId.Free),
            info: t('Rich design of the booking form description'),
            url: featurePath('custom-design-for-your-booking-calendar'),
          },
          {
            title: t('Change the URL of the booking page'),
            description: t('Change the URL of the booking page description'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Event type restricts'),
            checks: getAvailables(PlanId.Pro),
            info: t('Event type restricts description'),
          },
          {
            title: t('Custom branded pages'),
            checks: getAvailables(PlanId.Pro),
          },
          {
            title: t('Premium theme of the booking calendar'),
            checks: getAvailables(PlanId.Pro),
            info: t('Premium theme of the booking calendar description'),
            url: helpPath('7155141'),
          },
          {
            title: t('Remove Jicoo branding'),
            checks: getAvailables(JicooBrandingFeature.currentPlanId),
            info: t('Remove Jicoo branding description'),
          },
          {
            title: t('Custom email notifications'),
            checks: getAvailables(PlanId.Team),
          },
          {
            title: t('Make the guests visible'),
            checks: getAvailables(PlanId.Enterprice),
            info: t('Make the guests visible description'),
          },
        ],
      },
      {
        title: 'Integrations',
        functions: [
          {
            title: t('Connect to Google Calendar'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Connect to Outlook'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Connect to Apple Calendar'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Connect to Slack'),
            checks: getAvailables(PlanId.Free),
            info: t('Connect to Slack description'),
          },
          {
            title: t('Connect to Notion'),
            checks: getAvailables(PlanId.Free),
            info: t('Connect to Notion description'),
          },
          {
            title: t('Connect to HubSpot'),
            checks: getAvailables(PlanId.Free),
            info: t('Connect to HubSpot description'),
          },
          {
            title: t('Connect to Zoom'),
            checks: getAvailables(PlanId.Free),
            info: t('Connect to Zoom description'),
          },
          {
            title: t('Connect to Google Meet'),
            checks: getAvailables(PlanId.Free),
            info: t('Connect to Google Meet description'),
          },
          {
            title: t('Connect to Microsoft Teams'),
            checks: getAvailables(PlanId.Free),
            info: t('Connect to Microsoft Teams description'),
          },
          // {
          //   title: t('Connect to Google Sheets'),
          //   checks: getAvailables(PlanId.Pro),
          //   info: t('Connect to Google Sheets description'),
          // },
          {
            title: t('Track with Google Analytics'),
            checks: getAvailables(PlanId.Team),
            info: t('Track with Google Analytics description'),
          },
          {
            title: t('Connect to Meta Pixel'),
            checks: getAvailables(PlanId.Team),
            info: t('Connect to Meta Pixel description'),
          },
          {
            title: t('Connect to Stripe'),
            checks: getAvailables(PlanId.Team),
            info: t('Connect to Stripe description'),
          },
          {
            title: t('Build custom integration'),
            checks: getAvailables(PlanId.Team),
            info: t('Build custom integration description'),
          },
          {
            title: t('Connect to Salesforce'),
            checks: getAvailables(SalesforceIntegrationFeature.currentPlanId),
            info: t('Connect to Salesforce description'),
          },
          {
            title: t('Build custom integration'),
            checks: getAvailables(PlanId.Team),
            info: t('Build custom integration description'),
            url: developerApiWebhookPath(locale),
          },
          {
            title: t('Google workspace integration'),
            checks: getAvailables(PlanId.Team),
            info: t('Google workspace integration description'),
            url: helpPath('7251954'),
          },
          {
            title: t('Get reminders via SMS'),
            checks: getAvailables(PlanId.Enterprice),
            info: t('Get reminders via SMS description'),
          },
          {
            title: t('Account link'),
            checks: getAvailables(PlanId.Enterprice),
            info: t('Account link description'),
          },
        ],
      },
      {
        title: 'Admin',
        functions: [
          {
            title: t('Self-serve help center'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Live chat support'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Google cloud level security'),
            info: t('Google cloud level security description', {
              url: 'https://cloud.google.com/security',
            }),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t(`List of member's app integration status`),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Audit Log'),
            checks: ['', '', 30, 366],
            url: helpPath('7199247'),
          },
          {
            title: t('IP banning'),
            checks: getAvailables(PlanId.Enterprice),
            info: t('IP banning description'),
          },
          {
            title: t('SAML'),
            checks: getAvailables(PlanId.Enterprice),
          },
        ],
      },
      {
        title: 'Developer',
        functions: [
          {
            title: t('REST API'),
            checks: getAvailables(PlanId.Free),
          },
          {
            title: t('Live chat support'),
            checks: getAvailables(PlanId.Team),
          },
        ],
      },
    ],
    [],
  );

  const onTooltipClose = () => {
    setAnchor(null);
    setDescription(null);
  };

  const onTooltipOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    text?: string,
    url?: string,
  ) => {
    setAnchor(text ? event.currentTarget : null);
    let d = null;
    if (text) {
      d = text;
      if (url) {
        const href = locale !== defaultLocale ? `/${locale}${url}` : `${url}`;
        d += ` <a href="${href}" target="_blank">${t('Learn more')}</a>`;
      }
    }
    setDescription(d);
  };

  return {
    state: {
      plans,
      sections,
      anchor,
      description,
      locale,
    },
    actions: {
      onTooltipClose,
      onTooltipOpen,
    },
  };
}
